<template>
  <BaseModal
    v-model="localOpen"
    :title="modalTitle"
    size="lg"
    :close-on-backdrop="true"
    :show-close-icon="true"
    @close="handleClose"
  >
    <div class="modal-body">
      <div class="form-group">
        <label for="newDate">Fecha:</label>
        <input
          class="form-control"
          type="date"
          id="newDate"
          v-model="band.project.project_date"
        />
      </div>

      <div class="form-group">
        <label for="newEndDate">Fecha Estimada de Entrega:</label>
        <input
          class="form-control"
          type="date"
          id="newEndDate"
          v-model="band.project.eta"
        />
      </div>

      <div class="form-group">
        <label for="rape">RAPE:</label>
        <select id="rape" class="form-control" v-model="band.project.rape_id">
          <option
            v-for="opt in rapeEmployees"
            :key="opt.id"
            :value="opt.id"
            style="color: black"
          >
            {{ opt.name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label for="comment">Comentario:</label>
        <textarea
          class="form-control"
          id="comment"
          v-model="band.project.audit_comment"
          rows="4"
        />
      </div>
    </div>

    <template #footer>
      <slot name="footer">
        <button
          type="button"
          class="btn btn-outline-danger btn-long"
          @click="handleClose"
        >
          Cerrar
        </button>
        <button
          type="button"
          class="btn btn-outline-light btn-long"
          @click="validateDriveFolders"
        >
          Guardar
        </button>
      </slot>
    </template>
  </BaseModal>
</template>

<script setup>
import {
  defineProps,
  defineEmits,
  reactive,
  watch,
  ref,
  toRaw,
  onMounted,
  computed,
} from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import BaseModal from "./BaseModal.vue";

const store = useStore();

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  projectData: { type: Object, default: () => ({}) },
  state: { type: String, default: "" },
  title: { type: String, default: "Empezar Propuesta" },
});

const statesNames = {
  start_proposal: "Empezar Propuesta",
  work_in_progress: "Se Acepta la Propuesta",
  finish_work: "Terminar Desarrollo",
  client_approves: "Cliente Aprueba Desarrollo",
  client_not_approves: "Cliente No Aprueba Desarrollo",
  incidence_found: "Se Encontró Incidencia",
};

const modalTitle = computed(() => statesNames[props.state] || "Requerimiento");

const emit = defineEmits(["update:modelValue", "close"]);

const localOpen = computed({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

const handleClose = async () => {
  emit("update:modelValue", false);
  emit("close");
};

const band = reactive({
  project: {
    id: null,
    project_date: "",
    audit_comment: "",
    rape_id: "",
    eta: "",
  },
  title: "Empezar Propuesta",
  state: "",
  estimatedHourCount: 0,
  estimatedHoursCount: 0,
});

watch(
  () => props.state,
  (val) => {
    if (val != null) band.state = val;
  },
  { immediate: true }
);

watch(
  () => props.projectData,
  (p) => {
    if (!p) return;
    band.project.id = p.id ?? band.project.id;
    band.project.project_date = p.created_at ?? "";
    band.project.eta = p.eta ?? "";
    band.project.rape_id = p.rape_id ?? "";
    band.project.audit_comment = p.audit_comment ?? "";
  },
  { immediate: true, deep: true }
);

const rapeEmployees = ref([]);

const fetchRapeEmployees = async () => {
  try {
    const res = await store.dispatch("fetchRapeEmployees");
    rapeEmployees.value = res.employees;
  } catch {
    // manejar en silencio
  }
};

const validateDriveFolders = () => {
  const fodersByState = {
    "Empezar Propuesta": ["Requerimientos"],
    "Se Acepta la Propuesta": [
      "Propuesta",
      "Autorización del Cliente y OC",
      "Plan de Trabajo",
      "Análisis",
    ],
    "Terminar Desarrollo": ["Desarrollo y Pruebas"],
    "Cliente Aprueba Desarrollo": ["Validación del Cliente"],
  };

  const folder = fodersByState[modalTitle.value];
  if (folder) {
    validarCarpetas(folder);
  } else {
    transitionToNewState();
  }
};

const validarCarpetas = (folderNames) => {
  store.dispatch("app/showLoading");

  const driveID = props.projectData.drive_root_id;
  store
    .dispatch("retrieveFilesInFolder", driveID)
    .then(function (response) {
      let folders = response.data.files.filter(function (item) {
        return (
          item.mimeType === "application/vnd.google-apps.folder" && item.name
        );
      });

      let errores = [];

      let checks = folderNames.map(function (folderName) {
        return new Promise(function (resolve) {
          let foundFolders = folders.filter(function (folder) {
            const lastPart = folder.name.split("_").pop();
            return lastPart === folderName;
          });

          if (foundFolders.length === 0) {
            errores.push(`No se encontró la carpeta ${folderName}`);
            return resolve(false);
          }

          Promise.all(
            foundFolders.map(function (carpeta) {
              return store
                .dispatch("retrieveFilesInFolder", carpeta.id)
                .then(function (subResponse) {
                  let archivos = subResponse.data.files.filter(function (file) {
                    return (
                      file.mimeType !== "application/vnd.google-apps.folder"
                    );
                  });
                  if (archivos.length <= 0) {
                    errores.push(`${carpeta.name} está vacía.`);
                    return false;
                  }
                  return true;
                });
            })
          )
            .then(function () {
              resolve(true);
            })
            .catch(function () {
              resolve(false);
            });
        });
      });

      Promise.all(checks)
        .then(function () {
          if (errores.length > 0) {
            // const mensaje = `<ul style="list-style-type:none">${errores
            //   .map((e) => `<li style="padding-top: 10px">${e}</li>`)
            //   .join("")}</ul>`;
            toast.error(errores, {
              autoClose: 4000,
              theme: "dark",
            });
            store.dispatch("app/hideLoading");
          } else {
            store.dispatch("app/hideLoading");
            transitionToNewState();
          }
        })
        .catch(function () {
          store.dispatch("app/hideLoading");
        });
    })
    .catch(function () {
      toast.error("No se pudo obtener la información de Drive.", {
        autoClose: 4000,
        theme: "dark",
      });
      store.dispatch("app/hideLoading");
    });
};

const fetchProjects = async () => {
  store.dispatch("app/showLoading");
  try {
    await store.dispatch("fetchProjects");
  } finally {
    store.dispatch("app/hideLoading");
  }
};

const transitionToNewState = async () => {
  store.dispatch("app/showLoading");
  try {
    const res = await store.dispatch("transitionProjectState", {
      projectId: band.project.id,
      payload: toRaw(band),
    });
    await handleClose();
    if (res.data.notice) {
      await fetchProjects()
      await toast.success(res.data.notice, { autoClose: 4000, theme: "dark" });
    }
  } catch {
    //
  } finally {
    store.dispatch("app/hideLoading");
  }
};

onMounted(fetchRapeEmployees);
</script>

<style scoped>
.btn-long {
  width: 100px;
}
.modal-body .form-group {
  margin-bottom: 12px;
}
</style>
